import { Integration_Auth_Names_Enum, Integration_Names_Enum } from '@main/graphql/types.generated';
import { z } from 'zod/v4';

import type { IntegrationAuth, IntegrationAuthGroup } from './integration-auth';
import { IntegrationAuthType } from './integration-auth';

export const nameSchema = z.string().trim().min(1);

export enum IntegrationConnectorCategory {
  Agreements = 'agreements',
  Cloud = 'cloud',
  Identity = 'identity',
  DevOps = 'devops',
  Security = 'security',
  Endpoint = 'endpoint',
  Monitoring = 'monitoring',
  Productivity = 'productivity',
  ITSM = 'itsm',
  Support = 'support',
}

export interface IntegrationConnector<
  TAuthGroup extends IntegrationAuthGroup = IntegrationAuthGroup,
> {
  readonly ['_']: {
    Config: TAuthGroup['_']['Config'];
    NamedConfig: TAuthGroup['_']['NamedConfig'];
  };

  provider: Integration_Names_Enum;
  logoPath: string;
  categories: readonly [IntegrationConnectorCategory, ...IntegrationConnectorCategory[]];
  auths: TAuthGroup;
}

export function make<TAuthGroup extends IntegrationAuthGroup>(
  value: Omit<IntegrationConnector<TAuthGroup>, '_'>,
) {
  return value as IntegrationConnector<TAuthGroup>;
}

export function findAuthByType<T extends IntegrationAuthType>(
  connector: IntegrationConnector,
  authType: T,
) {
  return connector.auths.group.find(
    (auth): auth is IntegrationAuth & { type: T } => auth.type === authType,
  );
}

export function getAuthByType<T extends IntegrationAuthType>(
  connector: IntegrationConnector,
  authType: T,
) {
  const auth = findAuthByType(connector, authType);
  if (!auth) {
    throw new Error(
      `Could not find auth with the type "${authType}" in "${connector.provider}" connector`,
    );
  }
  return auth;
}

export function findAuthByName(
  connector: IntegrationConnector,
  authName: Integration_Auth_Names_Enum,
) {
  return connector.auths.group.find((auth) => auth.name === authName);
}

export function getAuthByName(
  connector: IntegrationConnector,
  authName: Integration_Auth_Names_Enum,
) {
  const auth = findAuthByName(connector, authName);
  if (!auth) {
    throw new Error(
      `Could not find auth with the name "${authName}" in "${connector.provider}" connector`,
    );
  }
  return auth;
}
